/**
 * Central click-action dispatcher.
 *
 * Single `document.addEventListener('click', ...)` registered as a
 * side-effect of importing this module. The dispatcher matches the
 * nearest `[data-action]` ancestor of the click target, looks up its
 * named handler in HANDLERS, and invokes it.
 *
 * Per-domain handlers live in:
 *   - `actions/auth.ts`  — open-login, logout, delete-account-request
 *   - `actions/esim.ts`  — open-topup, add-new-esim, get-esim
 *   - `actions/ui.ts`    — open-lang, toggle-mobile-menu, cookie-essential/all
 *
 * Adding a new action:
 *   1. Write its handler in the appropriate sub-module
 *   2. Add an entry to HANDLERS below
 *   3. Wire markup `data-action="<name>"` wherever it should fire
 *
 * Originally inline in `main.ts` lines 100-277. Phase 2.3 split 2026-05-12.
 */
import {
  openLoginAction,
  logoutAction,
  deleteAccountRequestAction,
} from './auth';
import {
  openTopupAction,
  addNewEsimAction,
  getEsimAction,
} from './esim';
import {
  openLangAction,
  toggleMobileMenuAction,
  cookieChoiceAction,
} from './ui';

type ActionHandler = (e: MouseEvent, target: HTMLElement) => void;

const HANDLERS: Record<string, ActionHandler> = {
  // auth
  'open-login': openLoginAction,
  'logout': logoutAction,
  'delete-account-request': deleteAccountRequestAction,
  // eSIM flows
  'open-topup': openTopupAction,
  'add-new-esim': addNewEsimAction,
  'get-esim': getEsimAction,
  // UI chrome
  'open-lang': openLangAction,
  'toggle-mobile-menu': toggleMobileMenuAction,
  'cookie-essential': cookieChoiceAction,
  'cookie-all': cookieChoiceAction,
};

document.addEventListener('click', (e) => {
  // `target` (not `t`) — `t` is the i18n function. Shadowing it here used
  // to mask `t('...')` calls below as method invocations on an HTMLElement.
  const target = (e.target as Element | null)?.closest<HTMLElement>('[data-action]');
  if (!target) return;
  const action = target.dataset.action;
  if (!action) return;
  const handler = HANDLERS[action];
  if (handler) handler(e, target);
});
